import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useExperience } from '../../contexts/ExperienceContext';
import { improvementPriorities, selectionPriorities } from '../../data/journeys';
import { needById } from '../../data/needs';
import { brand } from '../../data/palette';
import { ChoiceList } from '../ui/ChoiceList';
import { Eyebrow, Stage, StageTitle } from '../ui/Stage';
import type { Choice } from '../../types/filtek';
interface PriorityStageProps {
  variant: 'better' | 'new';
}
const copy = {
  better: {
    eyebrow: 'Where it costs you',
    question: 'What would you most like to improve?',
    note: 'Choose the one that costs you most — not all of them.'
  },
  new: {
    eyebrow: 'Your starting point',
    question: 'What matters most when choosing a restorative system?',
    note: 'One answer is enough. It shapes everything we show you next.'
  }
};

/** A single, useful question — the answer is carried through the whole session. */
export function PriorityStage({
  variant
}: PriorityStageProps) {
  const {
    signals,
    update,
    next,
    direction
  } = useExperience();
  const choices: Choice[] = variant === 'better' ? improvementPriorities : selectionPriorities;
  const [pending, setPending] = useState<string | undefined>();
  const [preview, setPreview] = useState<Choice | undefined>();
  const activeId = pending ?? signals.priority;
  const activeChoice = choices.find(choice => choice.id === activeId);
  const focusNeed = preview?.need ?? activeChoice?.need;
  const accent = focusNeed ? needById(focusNeed).accent : brand.green;
  const pick = (choice: Choice) => {
    if (pending) return;
    setPending(choice.id);
    update({
      priority: choice.id,
      focusNeed: undefined
    });
    window.setTimeout(next, 640);
  };
  return <Stage direction={direction} className="min-h-screen py-28 sm:py-32" labelledBy="priority-title" data-id="element-299" data-mp="w7riofwd6xp5">
      <div className="grid gap-12 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:items-start lg:gap-20" data-id="element-300" data-mp="3utugk6h92syf">
        <div className="lg:sticky lg:top-36" data-id="element-301" data-mp="170b7tcq7f21n">
          <Eyebrow accent={accent} data-id="element-302" data-mp="16z057g8hw0c4">{copy[variant].eyebrow}</Eyebrow>
          <StageTitle id="priority-title" size="display-sm" className="mt-7 max-w-md" data-id="element-303" data-mp="121q8dfis0me9">
            {copy[variant].question}
          </StageTitle>
          <p className="mt-7 max-w-sm text-base font-light leading-relaxed text-mute" data-id="element-304" data-mp="1fs3c15omxexs">
            {copy[variant].note}
          </p>
          <motion.p className="label-mono mt-10 text-[10px] text-mute/60" animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 3.4,
          repeat: Infinity
        }} data-id="element-305" data-mp="o1fhichgv7t3">
            Select one
          </motion.p>
        </div>

        <ChoiceList choices={choices} selected={activeId} accent={accent} onSelect={pick} onHover={setPreview} data-id="element-306" data-mp="x3eyj4shebw1" />
      </div>
    </Stage>;
}